import { useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { SearchInput } from '@/components/ui/search-input';
import { Pagination } from '@/components/ui/pagination';
import { assetKitsService, AssetKit } from '@/features/assets/services/asset-kits.service';
import { assetsService } from '@/features/assets/services/assets.service';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export const Route = createFileRoute('/_authenticated/asset-kits' as any)({
  component: AssetKitsPage,
});

function AssetKitsPage() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [search, setSearch] = useState('');
  const [selectedKitId, setSelectedKitId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [assetId, setAssetId] = useState('');
  const [quantityInKit, setQuantityInKit] = useState(1);

  const { data } = useQuery({
    queryKey: ['asset-kits', page, limit, search],
    queryFn: () => assetKitsService.getAll({ page, limit, search: search || undefined }),
  });
  const { data: assetsData } = useQuery({
    queryKey: ['assets-for-kit-link'],
    queryFn: () => assetsService.getAllPaginated({ page: 1, limit: 200 }),
  });

  const selectedKit = useMemo(
    () => data?.items.find((kit) => kit.id === selectedKitId) || null,
    [data, selectedKitId],
  );

  const createMutation = useMutation({
    mutationFn: () => assetKitsService.create({ name, description }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['asset-kits'] });
      setName('');
      setDescription('');
    },
  });
  const updateMutation = useMutation({
    mutationFn: (kit: AssetKit) =>
      assetKitsService.update(kit.id, {
        name,
        description,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['asset-kits'] }),
  });
  const deleteMutation = useMutation({
    mutationFn: (id: string) => assetKitsService.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['asset-kits'] });
      if (selectedKitId) setSelectedKitId(null);
    },
  });
  const addItemMutation = useMutation({
    mutationFn: () =>
      selectedKitId
        ? assetKitsService.upsertItem(selectedKitId, { assetId, quantityInKit })
        : Promise.reject(new Error('Selecione um kit')),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['asset-kits'] });
      setAssetId('');
      setQuantityInKit(1);
    },
  });
  const removeItemMutation = useMutation({
    mutationFn: (itemId: string) =>
      selectedKitId
        ? assetKitsService.removeItem(selectedKitId, itemId)
        : Promise.reject(new Error('Selecione um kit')),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['asset-kits'] }),
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Kits de Equipamentos</h1>
        <p className="text-muted-foreground">
          Crie kits, gerencie os itens e mantenha histórico de alterações.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Criar / editar kit</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-3 md:grid-cols-3">
          <Input placeholder="Nome do kit" value={name} onChange={(e) => setName(e.target.value)} />
          <Input
            placeholder="Descrição"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <div className="flex gap-2">
            <Button disabled={!name} onClick={() => createMutation.mutate()}>
              Criar kit
            </Button>
            {selectedKit && (
              <Button variant="outline" onClick={() => updateMutation.mutate(selectedKit)}>
                Atualizar kit
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Lista de kits</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <SearchInput
            placeholder="Pesquisar kit por nome..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
          <div className="space-y-2">
            {(data?.items || []).map((kit) => (
              <div
                key={kit.id}
                className={`rounded border p-3 ${selectedKitId === kit.id ? 'border-primary' : ''}`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium">{kit.name}</div>
                    <div className="text-sm text-muted-foreground">{kit.description || '-'}</div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSelectedKitId(kit.id);
                        setName(kit.name);
                        setDescription(kit.description || '');
                      }}
                    >
                      Selecionar
                    </Button>
                    <Button variant="destructive" size="sm" onClick={() => deleteMutation.mutate(kit.id)}>
                      Excluir
                    </Button>
                  </div>
                </div>
                {selectedKitId === kit.id && (
                  <div className="mt-3 space-y-2">
                    <Label>Itens do kit</Label>
                    <div className="grid gap-2 md:grid-cols-3">
                      <select
                        className="h-10 rounded-md border px-3"
                        value={assetId}
                        onChange={(e) => setAssetId(e.target.value)}
                      >
                        <option value="">Selecione um equipamento</option>
                        {(assetsData?.items || []).map((asset) => (
                          <option key={asset.id} value={asset.id}>
                            {asset.name}
                          </option>
                        ))}
                      </select>
                      <Input
                        type="number"
                        min={1}
                        value={quantityInKit}
                        onChange={(e) => setQuantityInKit(Number(e.target.value || 1))}
                      />
                      <Button
                        onClick={() => addItemMutation.mutate()}
                        disabled={!assetId || quantityInKit < 1}
                      >
                        Adicionar/Atualizar item
                      </Button>
                    </div>

                    {(kit.items || []).length === 0 ? (
                      <p className="text-sm text-muted-foreground">Sem itens neste kit.</p>
                    ) : (
                      <div className="space-y-1">
                        {(kit.items || []).map((item) => (
                          <div key={item.id} className="flex items-center justify-between text-sm">
                            <span>
                              {item.asset?.name || item.assetId} (qtd: {item.quantityInKit})
                            </span>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeItemMutation.mutate(item.id)}
                            >
                              Remover
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
          <Pagination total={data?.total || 0} page={page} limit={limit} onPageChange={setPage} />
        </CardContent>
      </Card>
    </div>
  );
}
